import { gridConfig } from 'config/gridConfig';
import { CoordinatePoint } from 'types/point';

/**
 * calculate snap point (if the point near grid point)
 * we snap independently by x and y
 */
export const getSnappedCoordinatePointToGrid = (p: CoordinatePoint, snapDivision: number): CoordinatePoint => {
  const snapSize = gridConfig.gridSize / (snapDivision * gridConfig.gridSizeInFeet);
  const snapTolerance = snapSize / 2;

  // reminder
  const r = {
    x: p.x % snapSize,
    y: p.y % snapSize,
  };

  // reminder's absolute values
  const absR = {
    x: Math.abs(r.x),
    y: Math.abs(r.y),
  };

  // sign of the reminder
  const signR = {
    x: Math.sign(r.x),
    y: Math.sign(r.y),
  };

  // previous point on the grid
  // i.e. if p is {0.1, 1.1}, grid point will be {0.0, 1.0}
  const gridPoint = {
    x: p.x - r.x,
    y: p.y - r.y,
  };

  // we return point itself if we don't snap
  const snapped = { ...p };

  if (absR.x < snapTolerance) {
    snapped.x = gridPoint.x;
  } else if (snapSize - absR.x <= snapTolerance) {
    snapped.x = gridPoint.x + snapSize * signR.x;
  }

  if (absR.y < snapTolerance) {
    snapped.y = gridPoint.y;
  } else if (snapSize - absR.y <= snapTolerance) {
    snapped.y = gridPoint.y + snapSize * signR.y;
  }

  return snapped;
};

// SNAP Point p into the precision grid
export const getSnappedToGrid = <T extends CoordinatePoint>(p: T, snapDivision: number, alignedPoints: T[] = []): T => {
  let snappedPosition = getSnappedCoordinatePointToGrid({ x: p.x, y: p.y }, snapDivision);

  if (alignedPoints.length > 0) {
    snappedPosition = snapAlsoToAlignment(snappedPosition, p, alignedPoints);
  }

  return {
    ...p,
    x: snappedPosition.x,
    y: snappedPosition.y,
  };
};

const snapAlsoToAlignment = <T extends CoordinatePoint>(snappedPosition: T, unSnappedPosition: T, alignedPointsList: T[]): T => {
  const mainPoint = { ...snappedPosition };

  for (let i = 0; i < alignedPointsList.length; i++) {
    const alignedPoint = alignedPointsList[i];
    // check with the unsnapped value, to avoid the chance of snapping into a position equally distant to the alignment.
    const horizontalDelta = Math.abs(unSnappedPosition.x - alignedPoint.x);
    const verticalDelta = Math.abs(unSnappedPosition.y - alignedPoint.y);

    if (horizontalDelta > verticalDelta) {
      // If aligned on the x axis, snap the y to stay in alignment
      mainPoint.y = alignedPoint.y;
    } else {
      // If aligned on the y axis, snap the x to stay in alignment
      mainPoint.x = alignedPoint.x;
    }
  }

  return mainPoint;
};
